import React, { useState, useRef, useCallback } from 'react';
import { pdfToolsAPI } from '../api';
import {
  Upload, FileText, Trash2, ChevronUp, ChevronDown,
  Download, Loader2, Minimize2, Merge
} from 'lucide-react';

function formatSize(bytes) {
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return bytes + ' B';
}

export default function PdfTools() {
  // ─── Compress State ───────────────────────────────────────────
  const [compressFile, setCompressFile] = useState(null);
  const [compressText, setCompressText] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [compressResult, setCompressResult] = useState(null);
  const [compressDrag, setCompressDrag] = useState(false);
  const [compressError, setCompressError] = useState('');
  const compressInputRef = useRef(null);

  // ─── Merge State ──────────────────────────────────────────────
  const [mergeFiles, setMergeFiles] = useState([]);
  const [merging, setMerging] = useState(false);
  const [mergeDrag, setMergeDrag] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const mergeInputRef = useRef(null);

  // ─── Compress Handlers ────────────────────────────────────────
  const handleCompressFileSelect = useCallback((file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setCompressError('Please upload a PDF file');
      return;
    }
    setCompressFile(file);
    setCompressError('');
    setCompressResult(null);
  }, []);

  const handleCompressDrop = useCallback((e) => {
    e.preventDefault();
    setCompressDrag(false);
    const file = e.dataTransfer.files[0];
    if (file) handleCompressFileSelect(file);
  }, [handleCompressFileSelect]);

  const handleCompress = async () => {
    if (!compressFile) return;
    setCompressing(true);
    setCompressError('');
    setCompressResult(null);

    try {
      const formData = new FormData();
      formData.append('file', compressFile);
      if (compressText.trim()) {
        formData.append('text', compressText.trim());
      }

      const response = await pdfToolsAPI.compress(formData);
      const blob = new Blob([response.data], { type: 'application/pdf' });

      const originalSize = parseInt(response.headers['x-original-size'] || compressFile.size);
      const compressedSize = blob.size;

      setCompressResult({ originalSize, compressedSize, blob });

      // Auto-download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compressed_${compressFile.name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setCompressError(err.response?.data?.error || 'Compression failed. Please try again.');
    } finally {
      setCompressing(false);
    }
  };

  // ─── Merge Handlers ───────────────────────────────────────────
  const handleMergeFilesSelect = useCallback((files) => {
    const pdfFiles = Array.from(files).filter(f => f.type === 'application/pdf');
    if (pdfFiles.length === 0) {
      setMergeError('Please upload PDF files only');
      return;
    }
    setMergeFiles(prev => [...prev, ...pdfFiles.map((f, i) => ({ id: Date.now() + i, file: f }))]);
    setMergeError('');
  }, []);

  const handleMergeDrop = useCallback((e) => {
    e.preventDefault();
    setMergeDrag(false);
    handleMergeFilesSelect(e.dataTransfer.files);
  }, [handleMergeFilesSelect]);

  const removeMergeFile = (id) => {
    setMergeFiles(prev => prev.filter(f => f.id !== id));
  };

  const moveMergeFile = (index, direction) => {
    const newFiles = [...mergeFiles];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newFiles.length) return;
    [newFiles[index], newFiles[targetIndex]] = [newFiles[targetIndex], newFiles[index]];
    setMergeFiles(newFiles);
  };

  const handleMerge = async () => {
    if (mergeFiles.length < 2) {
      setMergeError('Please add at least 2 PDF files to merge');
      return;
    }
    setMerging(true);
    setMergeError('');

    try {
      const formData = new FormData();
      mergeFiles.forEach((item) => {
        formData.append('files', item.file);
      });
      // Order is the natural order since user already reordered in UI
      const order = mergeFiles.map((_, i) => i);
      formData.append('order', JSON.stringify(order));

      const response = await pdfToolsAPI.merge(formData);
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Auto-download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'merged.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setMergeError(err.response?.data?.error || 'Merge failed. Please try again.');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ─── PDF Compress Section ──────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Minimize2 size={20} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-800">PDF Compress (Reduce Size)</h2>
            <p className="text-sm text-gray-500">Compress PDF and optionally add custom text page</p>
          </div>
        </div>

        {/* Drop Zone */}
        <div
          onDrop={handleCompressDrop}
          onDragOver={(e) => { e.preventDefault(); setCompressDrag(true); }}
          onDragLeave={() => setCompressDrag(false)}
          onClick={() => compressInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            compressDrag
              ? 'border-blue-500 bg-blue-50'
              : compressFile
              ? 'border-green-400 bg-green-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
          }`}
        >
          <input
            ref={compressInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={(e) => handleCompressFileSelect(e.target.files[0])}
          />
          {compressFile ? (
            <div className="space-y-2">
              <FileText size={40} className="mx-auto text-green-500" />
              <p className="font-semibold text-gray-700">{compressFile.name}</p>
              <p className="text-sm text-gray-500">Size: {formatSize(compressFile.size)}</p>
              <p className="text-xs text-gray-400">Click or drag to replace</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload size={40} className="mx-auto text-gray-400" />
              <p className="font-medium text-gray-600">Drag & Drop PDF here</p>
              <p className="text-sm text-gray-400">or click to browse</p>
            </div>
          )}
        </div>

        {/* Custom Text Input */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Custom Text (optional - added as first page)
          </label>
          <textarea
            value={compressText}
            onChange={(e) => setCompressText(e.target.value)}
            placeholder="Type any text to add as the first page of the PDF..."
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
          />
        </div>

        {/* Compress Result */}
        {compressResult && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-sm font-medium text-green-800">
              ✓ Compressed successfully!
            </p>
            <div className="flex gap-6 mt-2 text-sm text-green-700">
              <span>Original: <strong>{formatSize(compressResult.originalSize)}</strong></span>
              <span>Compressed: <strong>{formatSize(compressResult.compressedSize)}</strong></span>
              <span>
                Saved: <strong>
                  {Math.max(0, ((1 - compressResult.compressedSize / compressResult.originalSize) * 100)).toFixed(1)}%
                </strong>
              </span>
            </div>
          </div>
        )}

        {compressError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {compressError}
          </div>
        )}

        {/* Compress Button */}
        <button
          onClick={handleCompress}
          disabled={!compressFile || compressing}
          className="mt-4 w-full py-3 px-6 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed
            bg-gradient-to-b from-blue-500 to-blue-700 shadow-[0_4px_0_0_#1e40af,0_6px_12px_rgba(37,99,235,0.3)]
            hover:shadow-[0_2px_0_0_#1e40af,0_4px_8px_rgba(37,99,235,0.3)] hover:translate-y-[2px]
            active:shadow-[0_0px_0_0_#1e40af] active:translate-y-[4px]"
        >
          {compressing ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={18} className="animate-spin" /> Compressing...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Download size={18} /> Compress & Download
            </span>
          )}
        </button>
      </div>

      {/* ─── PDF Merge Section ─────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Merge size={20} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-800">PDF Merge</h2>
            <p className="text-sm text-gray-500">Combine multiple PDFs into one file</p>
          </div>
        </div>

        {/* Drop Zone */}
        <div
          onDrop={handleMergeDrop}
          onDragOver={(e) => { e.preventDefault(); setMergeDrag(true); }}
          onDragLeave={() => setMergeDrag(false)}
          onClick={() => mergeInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            mergeDrag
              ? 'border-blue-500 bg-blue-50'
              : mergeFiles.length > 0
              ? 'border-blue-300 bg-blue-50/30'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
          }`}
        >
          <input
            ref={mergeInputRef}
            type="file"
            accept=".pdf"
            multiple
            className="hidden"
            onChange={(e) => handleMergeFilesSelect(e.target.files)}
          />
          <Upload size={36} className="mx-auto text-gray-400 mb-2" />
          <p className="font-medium text-gray-600">Drag & Drop PDF files here</p>
          <p className="text-sm text-gray-400">or click to browse (multiple files)</p>
          {mergeFiles.length > 0 && (
            <p className="text-xs text-blue-600 mt-2 font-medium">{mergeFiles.length} file(s) added</p>
          )}
        </div>

        {/* File List */}
        {mergeFiles.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-gray-600 mb-2">Files to merge (drag order):</p>
            {mergeFiles.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200"
              >
                <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
                  {index + 1}
                </span>
                <FileText size={18} className="text-red-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{item.file.name}</p>
                  <p className="text-xs text-gray-400">{formatSize(item.file.size)}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); moveMergeFile(index, -1); }}
                    disabled={index === 0}
                    className="p-1.5 rounded-lg hover:bg-blue-100 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move up"
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); moveMergeFile(index, 1); }}
                    disabled={index === mergeFiles.length - 1}
                    className="p-1.5 rounded-lg hover:bg-blue-100 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move down"
                  >
                    <ChevronDown size={16} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeMergeFile(item.id); }}
                    className="p-1.5 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                    title="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {mergeError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {mergeError}
          </div>
        )}

        {/* Merge Button */}
        <button
          onClick={handleMerge}
          disabled={mergeFiles.length < 2 || merging}
          className="mt-4 w-full py-3 px-6 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed
            bg-gradient-to-b from-blue-500 to-blue-700 shadow-[0_4px_0_0_#1e40af,0_6px_12px_rgba(37,99,235,0.3)]
            hover:shadow-[0_2px_0_0_#1e40af,0_4px_8px_rgba(37,99,235,0.3)] hover:translate-y-[2px]
            active:shadow-[0_0px_0_0_#1e40af] active:translate-y-[4px]"
        >
          {merging ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={18} className="animate-spin" /> Merging...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Download size={18} /> Merge & Download
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
